import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, Image as ImageIcon, Trash2, Loader2, Send, CalendarClock, Eye, Save,
  Copy, FlaskConical, Users, MailOpen, Sparkles, Globe, Type, Mail,
  UserMinus, Undo2, CheckCircle2,
} from 'lucide-react';
import ScheduleModal from './ScheduleModal';
import {
  dmApi,
  AUDIENCE_TYPES,
  TICKET_STATUS_OPTIONS,
  OPEN_CALL_STATUS_OPTIONS,
  CURATOR_RATING_OPTIONS,
  GLOBAL_RATING_OPTIONS,
  ASSET_STATUS_OPTIONS,
  RELATIONSHIP_STATUS_OPTIONS,
  INVITE_CATEGORY_OPTIONS,
  INVITE_PROSPECT_STATUS_OPTIONS,
  PERSONALIZATION_TOKENS,
  SOURCE_BADGE_STYLE,
} from '../../lib/directMessagesApi';

const CTA_DEFAULT_URL = 'https://therelativegallery.com/';
const CTA_DEFAULT_LABEL = 'Visit The Relative Gallery';

// ─── Small visual atoms used only inside this composer ─────────────

function SectionCard({ title, helper, icon: Icon, children, testId }) {
  return (
    <section
      data-testid={testId}
      className="border border-[#161616] bg-[#070707] p-6 space-y-4"
    >
      <header className="flex items-start justify-between gap-3">
        <div>
          <h3 className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.32em] text-[#90E0EF]">
            {Icon && <Icon className="w-3.5 h-3.5" />}
            {title}
          </h3>
          {helper && (
            <p className="mt-1 text-[11px] font-mono text-[#666] leading-relaxed max-w-prose">
              {helper}
            </p>
          )}
        </div>
      </header>
      <div>{children}</div>
    </section>
  );
}

function FieldLabel({ children }) {
  return (
    <span className="block text-[10px] font-mono uppercase tracking-[0.22em] text-[#777] mb-2">
      {children}
    </span>
  );
}

function MultiSelectChips({ options, value, onChange, testId }) {
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId}>
      {options.map((opt) => {
        const active = value.includes(opt);
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(active ? value.filter((v) => v !== opt) : [...value, opt])}
            className={`px-2.5 py-1.5 text-[10px] font-mono uppercase tracking-[0.16em] border transition-all ${
              active
                ? 'border-[#90E0EF] text-[#90E0EF] bg-[#90E0EF]/10 shadow-[0_0_10px_rgba(144,224,239,0.18)]'
                : 'border-[#222] text-[#888] hover:text-white hover:border-[#3a3a3a]'
            }`}
          >
            {opt.replace(/_/g, ' ')}
          </button>
        );
      })}
    </div>
  );
}

function SourceBadge({ source }) {
  const style = SOURCE_BADGE_STYLE[source] || SOURCE_BADGE_STYLE.ticket;
  return (
    <span
      className="text-[9px] font-mono uppercase tracking-[0.16em] px-1.5 py-0.5 border whitespace-nowrap"
      style={{ color: style.color, borderColor: style.border, background: style.bg }}
    >
      {style.label}
    </span>
  );
}

function StatusPill({ status }) {
  const palette = {
    draft: { color: '#999', border: '#333', bg: 'transparent', label: 'Draft' },
    queued: { color: '#90E0EF', border: 'rgba(144,224,239,0.45)', bg: 'rgba(144,224,239,0.06)', label: 'Queued' },
    sending: { color: '#90E0EF', border: 'rgba(144,224,239,0.45)', bg: 'rgba(144,224,239,0.06)', label: 'Sending' },
    scheduled: { color: '#e4c07a', border: 'rgba(228,192,122,0.45)', bg: 'rgba(228,192,122,0.06)', label: 'Scheduled' },
    sent: { color: '#7ed4a0', border: 'rgba(126,212,160,0.45)', bg: 'rgba(126,212,160,0.06)', label: 'Sent' },
    partial: { color: '#e4c07a', border: 'rgba(228,192,122,0.45)', bg: 'rgba(228,192,122,0.06)', label: 'Partial' },
    failed: { color: '#e88ba0', border: 'rgba(232,139,160,0.45)', bg: 'rgba(232,139,160,0.06)', label: 'Failed' },
  };
  const p = palette[status] || palette.draft;
  return (
    <span
      className="text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-1 border"
      style={{ color: p.color, borderColor: p.border, background: p.bg }}
      data-testid="dm-status-pill"
    >
      {p.label}
    </span>
  );
}

function buildPayloadFrom(campaignData, manuallyPickedIds) {
  const c = campaignData || { audience_type: 'tickets' };
  const m = manuallyPickedIds || [];
  const filters = { ...(c.audience_filters || {}) };
  if (c.audience_type === 'custom') {
    filters.manual_ids = m.map((it) => ({ source_type: it.source_type, source_id: it.source_id }));
  } else {
    delete filters.manual_ids;
  }
  return {
    name: c.name || 'Untitled direct message',
    subject: c.subject || '',
    body_text: c.body_text || '',
    audience_type: c.audience_type,
    audience_filters: filters,
    hero_image_url: c.hero_image_url,
    cta_enabled: !!c.cta_enabled,
    cta_url: c.cta_url || CTA_DEFAULT_URL,
    cta_label: c.cta_label || CTA_DEFAULT_LABEL,
  };
}

async function performSaveDraft({
  campaign, manualIds, exhibitionId,
  silent, setCampaign, setSaving, onSaved,
}) {
  if (!campaign) return undefined;
  setSaving(true);
  try {
    const payload = buildPayloadFrom(campaign, manualIds);
    if (campaign.id) {
      const updated = await dmApi.update(campaign.id, payload);
      setCampaign(updated);
      if (!silent) toast.success('Draft saved');
      onSaved?.();
      return updated.id;
    }
    const created = await dmApi.create({ exhibition_id: exhibitionId, ...payload });
    setCampaign(created);
    if (!silent) toast.success('Draft saved');
    onSaved?.();
    return created.id;
  } catch (err) {
    toast.error(err.response?.data?.detail || 'Save failed');
    return undefined;
  } finally {
    setSaving(false);
  }
}

export default function ComposerDrawer({ open, exhibitionId, campaignId, onClose, onSaved }) {
  const [campaign, setCampaign] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [audiencePreview, setAudiencePreview] = useState(null); // {total, recipients}
  const [audiencePreviewLoading, setAudiencePreviewLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [testEmail, setTestEmail] = useState('mrrelativelabs@gmail.com');
  const [scheduling, setScheduling] = useState(false);
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const [scheduledLabel, setScheduledLabel] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [manualIds, setManualIds] = useState([]); // [{source_type, source_id, email, name}]
  const [uploadingHero, setUploadingHero] = useState(false);
  // ─── Audience confirmation gate ─────────────────────────────────
  // The curator must explicitly confirm "this is the final recipient
  // group" before Send Now / Schedule unlock. The confirmation is now a
  // server-side LOCK: clicking the checkbox calls POST
  // /confirm-audience which snapshots the resolved recipient list into
  // campaign.confirmed_audience. send-now and schedule read ONLY from
  // that snapshot — they NEVER re-resolve the live audience. Any
  // PATCH that touches audience_type or audience_filters wipes the
  // snapshot server-side, so the curator is forced to re-confirm
  // before another dispatch can fire.
  const lockedSnapshot = campaign?.confirmed_audience || null;
  const audienceConfirmed = !!lockedSnapshot;
  const [confirmBusy, setConfirmBusy] = useState(false);
  const fileInputRef = useRef(null);
  const bodyTextRef = useRef(null);

  // Snapshot helpers — we mutate `campaign` locally and PATCH on save.
  const patchLocal = (patch) => setCampaign((c) => (c ? { ...c, ...patch } : c));
  const patchFilters = (patch) =>
    setCampaign((c) => ({ ...c, audience_filters: { ...(c?.audience_filters || {}), ...patch } }));

  // Per-campaign exclusion list — kept in audience_filters.excluded_recipients
  // so it persists on Save Draft. Helpers match by lower(email) so the same
  // person referenced from multiple sources is excluded once.
  const excludedRecipients = campaign?.audience_filters?.excluded_recipients || [];
  const isExcluded = (email) => {
    const e = (email || '').toLowerCase();
    return excludedRecipients.some((r) => (r.email || '').toLowerCase() === e);
  };
  const excludeRecipient = (recipient) => {
    const email = (recipient?.email || '').toLowerCase();
    if (!email) return;
    if (isExcluded(email)) return;
    const next = [
      ...excludedRecipients,
      {
        source_type: recipient.source_type,
        source_id: recipient.source_id || null,
        email: recipient.email,
        name: recipient.name || null,
      },
    ];
    patchFilters({ excluded_recipients: next });
  };
  const restoreRecipient = (recipientOrEmail) => {
    const email = (typeof recipientOrEmail === 'string'
      ? recipientOrEmail
      : recipientOrEmail?.email || ''
    ).toLowerCase();
    if (!email) return;
    const next = excludedRecipients.filter((r) => (r.email || '').toLowerCase() !== email);
    patchFilters({ excluded_recipients: next });
  };
  const clearExclusions = () => patchFilters({ excluded_recipients: [] });

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    if (!campaignId) {
      setTimeout(() => {
        if (cancelled) return;
        setCampaign({
          id: null,
          name: '',
          subject: '',
          body_text: '',
          audience_type: 'tickets',
          audience_filters: {},
          hero_image_url: null,
          cta_enabled: false,
          cta_url: CTA_DEFAULT_URL,
          cta_label: CTA_DEFAULT_LABEL,
          status: 'draft',
        });
        setManualIds([]);
        setAudiencePreview(null);
      }, 0);
      return () => { cancelled = true; };
    }
    setTimeout(() => { if (!cancelled) setLoading(true); }, 0);
    dmApi.get(campaignId)
      .then((data) => {
        if (cancelled) return;
        setCampaign(data);
        const m = data.audience_filters?.manual_ids || [];
        setManualIds(m);
      })
      .catch(() => { if (!cancelled) toast.error('Could not load campaign'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, campaignId]);

  // Auto-refresh audience preview when audience filters change (debounced).
  useEffect(() => {
    if (!open || !campaign) return undefined;
    const t = setTimeout(() => {
      runAudiencePreview();
    }, 400);
    return () => clearTimeout(t);
  }, [open, campaign, manualIds]);

  // ─── Stale-lock warning ─────────────────────────────────────
  // We cache the audienceFingerprint AT THE MOMENT a new snapshot
  // arrives (keyed off ``confirmed_at`` so a re-confirm refreshes the
  // cache). Any subsequent local edit to the audience changes
  // ``audienceFingerprint`` but NOT the cached value — that divergence
  // is exactly the "you edited after confirming" signal we want to
  // warn on.
  const audienceFingerprint = JSON.stringify({
    type: campaign?.audience_type || null,
    filters: campaign?.audience_filters || {},
    manualIds: manualIds.map((m) => `${m.source_type}:${m.source_id}`),
  });
  const lockedFingerprintRef = useRef({ confirmed_at: null, fingerprint: null });
  useEffect(() => {
    if (!lockedSnapshot) {
      lockedFingerprintRef.current = { confirmed_at: null, fingerprint: null };
      return;
    }
    if (lockedSnapshot.confirmed_at !== lockedFingerprintRef.current.confirmed_at) {
      // Brand-new snapshot — cache its fingerprint as the reference.
      lockedFingerprintRef.current = {
        confirmed_at: lockedSnapshot.confirmed_at,
        fingerprint: audienceFingerprint,
      };
    }
  }, [lockedSnapshot, audienceFingerprint]);
  const showStaleWarning = !!lockedSnapshot
    && !!lockedFingerprintRef.current.fingerprint
    && audienceFingerprint !== lockedFingerprintRef.current.fingerprint;

  // ─── Confirmation handlers (server-side lock) ───────────────────
  // Tick checkbox → POST /confirm-audience → server snapshots the
  // resolved recipient list. Untick → DELETE /confirm-audience → wipe
  // the lock. Both reload the campaign so audienceConfirmed (which is
  // derived from `campaign.confirmed_audience`) stays truthful.
  const handleConfirmAudience = async () => {
    if (confirmBusy) return;
    if (!campaign) return;
    setConfirmBusy(true);
    try {
      // Save first so server resolves recipients against the latest
      // exclusions/manual_ids the curator can see in the preview.
      // For brand-new drafts (campaign.id === null), saveDraft also
      // CREATES the row and returns its new id — we use that here
      // instead of relying on async state to settle.
      const savedId = await saveDraft({ silent: true });
      const targetId = savedId || campaign.id;
      if (!targetId) {
        toast.error('Could not save the draft — fix any errors and try again.');
        return;
      }
      const result = await dmApi.confirmAudience(targetId);
      // Pull the fresh campaign so confirmed_audience is in state.
      const fresh = await dmApi.get(targetId);
      setCampaign(fresh);
      toast.success(`Recipient group locked · ${result.count} recipient${result.count === 1 ? '' : 's'}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not confirm audience';
      toast.error(msg);
    } finally {
      setConfirmBusy(false);
    }
  };

  const handleUnlockAudience = async () => {
    if (!campaign?.id || confirmBusy) return;
    setConfirmBusy(true);
    try {
      await dmApi.unlockAudience(campaign.id);
      const fresh = await dmApi.get(campaign.id);
      setCampaign(fresh);
      toast.success('Recipient group unlocked');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not unlock');
    } finally {
      setConfirmBusy(false);
    }
  };

  const runAudiencePreview = async () => {
    if (!campaign) return;
    setAudiencePreviewLoading(true);
    try {
      const filters = { ...(campaign.audience_filters || {}) };
      if (campaign.audience_type === 'custom') filters.manual_ids = manualIds;
      const data = await dmApi.audiencePreview({
        exhibition_id: exhibitionId,
        audience_type: campaign.audience_type,
        audience_filters: filters,
        manual_ids: manualIds,
      });
      setAudiencePreview(data);
    } catch (err) {
      setAudiencePreview({ total: 0, recipients: [], error: err.response?.data?.detail || 'failed' });
    } finally {
      setAudiencePreviewLoading(false);
    }
  };

  const saveDraft = async ({ silent } = {}) =>
    performSaveDraft({
      campaign, manualIds, exhibitionId,
      silent, setCampaign, setSaving, onSaved,
    });

  const ensureSavedNow = async () => {
    if (campaign && campaign.id) return campaign.id;
    return await saveDraft({ silent: true });
  };

  const openPreview = async () => {
    const id = await ensureSavedNow();
    if (!id) return;
    try {
      const data = await dmApi.preview(id);
      setPreviewHtml(data.html);
      setPreviewSubject(data.subject);
      setShowPreview(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Preview failed');
    }
  };

  const sendTest = async () => {
    if (!testEmail) return toast.error('Enter a test email');
    const id = await ensureSavedNow();
    if (!id) return;
    const toastId = toast.loading('Sending test email…');
    try {
      await dmApi.testSend(id, testEmail);
      toast.success(`Test sent to ${testEmail}`, { id: toastId });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Test send failed', { id: toastId });
    }
  };

  const schedule = async (isoString, meta) => {
    if (!isoString) return toast.error('Pick a date and time first');
    const id = await ensureSavedNow();
    if (!id) return;
    setScheduleBusy(true);
    try {
      await dmApi.schedule(id, isoString);
      const label = meta?.warsaw_display || new Date(isoString).toLocaleString();
      setScheduledLabel(label);
      toast.success(`Scheduled · ${label} (Warsaw time)`);
      setScheduling(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Schedule failed');
    } finally {
      setScheduleBusy(false);
    }
  };

  const sendNow = async () => {
    if (!window.confirm(`Send this campaign to ${audiencePreview?.total || 0} recipient(s)? This cannot be undone.`)) return;
    const id = await ensureSavedNow();
    if (!id) return;
    try {
      const res = await dmApi.sendNow(id);
      toast.success(`Queued ${res.total_recipients} recipient(s)`);
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Send failed');
    }
  };

  const insertVariable = (token) => {
    const el = bodyTextRef.current;
    const currentBody = campaign?.body_text || '';
    // If the textarea isn't focused / mounted, append the token to the end
    // with a separating space (instead of silently failing).
    if (!el || typeof el.selectionStart !== 'number') {
      const next = currentBody.length && !currentBody.endsWith(' ')
        ? `${currentBody} ${token}`
        : `${currentBody}${token}`;
      patchLocal({ body_text: next });
      return;
    }
    const start = el.selectionStart || 0;
    const end = el.selectionEnd || 0;
    const next = currentBody.slice(0, start) + token + currentBody.slice(end);
    patchLocal({ body_text: next });
    requestAnimationFrame(() => {
      el.focus();
      el.setSelectionRange(start + token.length, start + token.length);
    });
  };

  const uploadHero = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Only image files');
    if (file.size > 5 * 1024 * 1024) return toast.error('Max 5 MB');
    setUploadingHero(true);
    try {
      const target = await dmApi.uploadUrl({ exhibition_id: exhibitionId, content_type: file.type });
      const signed = target.signed;
      let publicUrl = target.public_url;
      if (signed?.signed_url) {
        // Direct PUT to Supabase signed URL.
        const put = await fetch(signed.signed_url, {
          method: 'PUT',
          headers: { 'Content-Type': file.type },
          body: file,
        });
        if (!put.ok) throw new Error(`Upload returned ${put.status}`);
      } else {
        throw new Error('No signed upload URL returned');
      }
      patchLocal({ hero_image_url: publicUrl });
      toast.success('Hero graphic uploaded');
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploadingHero(false);
    }
  };

  const removeHero = () => patchLocal({ hero_image_url: null });

  const handleSearch = async (q) => {
    setSearchQuery(q);
    if (!q || q.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await dmApi.search({ exhibition_id: exhibitionId, query: q, limit: 20 });
      setSearchResults(res.results || []);
    } catch {
      setSearchResults([]);
    }
  };

  const toggleManual = (row) => {
    setManualIds((cur) => {
      const idx = cur.findIndex((r) => r.source_type === row.source_type && r.source_id === row.source_id);
      if (idx >= 0) return cur.filter((_, i) => i !== idx);
      return [...cur, row];
    });
  };

  const removeManual = (row) => {
    setManualIds((cur) => cur.filter((r) => !(r.source_type === row.source_type && r.source_id === row.source_id)));
  };

  // ─── Render ───────────────────────────────────────────────────────

  if (!open) return null;

  const audienceType = campaign?.audience_type || 'tickets';
  const totalRecipients = audiencePreview?.total ?? null;

  return (
    <AnimatePresence>
      <motion.div
        key="dm-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-40 bg-black/85 backdrop-blur-md"
      />
      <motion.div
        key="dm-fullscreen"
        initial={{ opacity: 0, scale: 0.985, y: 6 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.99 }}
        transition={{ duration: 0.22, ease: 'easeOut' }}
        className="fixed inset-0 z-50 bg-[#050505] flex flex-col"
        data-testid="dm-composer-drawer"
      >
        {/* ─── Top header bar ─────────────────────────────────────────── */}
        <header className="flex items-center gap-6 px-8 py-5 border-b border-[#161616] bg-[#070707]">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 border border-[#90E0EF]/40 flex items-center justify-center text-[#90E0EF] bg-[rgba(144,224,239,0.04)]">
              <MailOpen className="w-4 h-4" />
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-[0.32em] text-[#90E0EF]">Direct Message</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#555] mt-0.5">
                {campaign?.id ? 'Editing campaign' : 'New campaign'}
              </div>
            </div>
          </div>

          <div className="flex-1 max-w-2xl">
            <FieldLabel>Internal name</FieldLabel>
            <input
              value={campaign?.name || ''}
              onChange={(e) => patchLocal({ name: e.target.value })}
              placeholder="e.g. June launch — accepted curators"
              className="w-full bg-transparent border-b border-[#222] text-white text-lg font-outfit focus:outline-none focus:border-[#90E0EF] pb-1 placeholder:text-[#3a3a3a] transition-colors"
              data-testid="dm-name"
            />
          </div>

          <div className="flex items-center gap-3">
            <StatusPill status={campaign?.status || 'draft'} />
            <button
              onClick={onClose}
              data-testid="dm-close"
              className="w-9 h-9 border border-[#222] flex items-center justify-center text-[#777] hover:text-white hover:border-[#444] transition-all"
              title="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </header>

        {/* ─── Two-column body ────────────────────────────────────────── */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="h-full flex items-center justify-center text-[#444] text-xs font-mono">
              <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading campaign…
            </div>
          ) : (
            <div className="h-full grid grid-cols-12 gap-0">
              {/* ─── LEFT — Content composer (7/12) ───────────────── */}
              <div className="col-span-12 lg:col-span-7 border-r border-[#161616] overflow-y-auto">
                <div className="max-w-3xl mx-auto px-8 py-8 space-y-6">
                  {/* Subject */}
                  <SectionCard title="Subject" icon={Mail} testId="dm-section-subject">
                    <input
                      value={campaign?.subject || ''}
                      onChange={(e) => patchLocal({ subject: e.target.value })}
                      placeholder="Welcome to The Relative Gallery"
                      className="w-full bg-transparent border-b border-[#222] text-white text-base font-outfit pb-2 focus:outline-none focus:border-[#90E0EF] placeholder:text-[#3a3a3a] transition-colors"
                      data-testid="dm-subject"
                    />
                    <p className="mt-2 text-[10px] font-mono text-[#555]">
                      Tip · subject also supports personalization tokens like {`{{name}}`}.
                    </p>
                  </SectionCard>

                  {/* Personalization tokens */}
                  <SectionCard
                    title="Personalization tokens"
                    icon={Sparkles}
                    helper="Click a token to insert it at the cursor inside the message body. Tokens are replaced per recipient at send time."
                    testId="dm-section-tokens"
                  >
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="dm-token-grid">
                      {PERSONALIZATION_TOKENS.map((t) => (
                        <button
                          key={t.token}
                          type="button"
                          onClick={() => insertVariable(t.token)}
                          data-testid={`dm-token-${t.token.replace(/[^a-z_]/gi, '')}`}
                          title={t.helper}
                          className="group text-left px-3 py-2 border border-[#222] hover:border-[#90E0EF] hover:bg-[rgba(144,224,239,0.05)] transition-all"
                        >
                          <div className="text-[11px] font-outfit text-white">{t.label}</div>
                          <div className="text-[10px] font-mono text-[#666] group-hover:text-[#90E0EF] transition-colors mt-0.5">
                            {t.token}
                          </div>
                        </button>
                      ))}
                    </div>
                  </SectionCard>

                  {/* Body */}
                  <SectionCard
                    title="Message body"
                    icon={Type}
                    helper="Write the message in plain text. The branded email layout is generated automatically."
                    testId="dm-section-body"
                  >
                    <textarea
                      ref={bodyTextRef}
                      value={campaign?.body_text || ''}
                      onChange={(e) => patchLocal({ body_text: e.target.value })}
                      placeholder={`Hi {{name}},\n\nWe are very glad to invite you to ...`}
                      rows={18}
                      className="w-full bg-[#050505] border border-[#1a1a1a] text-white text-[13px] font-outfit px-4 py-3 focus:outline-none focus:border-[#90E0EF] resize-y leading-[1.75] placeholder:text-[#3a3a3a]"
                      data-testid="dm-body"
                    />
                    <div className="mt-2 flex items-center justify-between text-[10px] font-mono text-[#555]">
                      <span>{(campaign?.body_text || '').length} chars</span>
                      <span>Branded layout is added automatically · paragraph breaks preserved</span>
                    </div>
                  </SectionCard>

                  {/* Hero graphic */}
                  <SectionCard
                    title="Hero graphic"
                    icon={ImageIcon}
                    helper="Optional image that appears beneath the header. Recommended 1200 × 600px JPG / PNG."
                    testId="dm-section-hero"
                  >
                    {campaign?.hero_image_url ? (
                      <div className="relative border border-[#222] group">
                        <img
                          src={campaign.hero_image_url}
                          alt="hero"
                          className="w-full max-h-72 object-cover"
                        />
                        <button
                          type="button"
                          onClick={removeHero}
                          className="absolute top-2 right-2 px-2 py-1 bg-black/80 border border-[rgba(232,139,160,0.45)] text-[#e88ba0] hover:text-white text-[10px] font-mono uppercase tracking-[0.18em] inline-flex items-center gap-1"
                          data-testid="dm-hero-remove"
                        >
                          <Trash2 className="w-3 h-3" /> Remove
                        </button>
                      </div>
                    ) : (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        onDragOver={(e) => { e.preventDefault(); }}
                        onDrop={(e) => { e.preventDefault(); uploadHero(e.dataTransfer.files?.[0]); }}
                        className="border border-dashed border-[#222] hover:border-[#90E0EF]/50 hover:bg-[rgba(144,224,239,0.03)] py-12 text-center cursor-pointer transition-all"
                        data-testid="dm-hero-drop"
                      >
                        {uploadingHero ? (
                          <span className="inline-flex items-center gap-2 text-[11px] font-mono text-[#90E0EF]">
                            <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
                          </span>
                        ) : (
                          <div>
                            <ImageIcon className="w-6 h-6 mx-auto text-[#444]" />
                            <p className="mt-3 text-[11px] font-mono text-[#777] uppercase tracking-[0.2em]">
                              Drag &amp; drop or click to upload
                            </p>
                            <p className="mt-1 text-[10px] font-mono text-[#444]">
                              PNG / JPG · up to 5 MB
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => uploadHero(e.target.files?.[0])}
                      className="hidden"
                    />
                  </SectionCard>

                  {/* CTA */}
                  <SectionCard
                    title="Website button"
                    icon={Globe}
                    helper="Optional dark CTA rendered beneath the body content."
                    testId="dm-section-cta"
                  >
                    <button
                      type="button"
                      onClick={() => patchLocal({ cta_enabled: !campaign?.cta_enabled })}
                      data-testid="dm-cta-toggle"
                      className={`w-full text-left px-4 py-3 border transition-all ${
                        campaign?.cta_enabled
                          ? 'border-[#90E0EF] bg-[rgba(144,224,239,0.05)]'
                          : 'border-[#222] hover:border-[#3a3a3a]'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-[12px] font-outfit text-white">Show website button</div>
                          <div className="text-[10px] font-mono text-[#666] mt-0.5">
                            Adds a square dark button linking to the gallery website.
                          </div>
                        </div>
                        <div
                          className={`w-9 h-5 border ${
                            campaign?.cta_enabled
                              ? 'border-[#90E0EF] bg-[rgba(144,224,239,0.18)]'
                              : 'border-[#333] bg-transparent'
                          } relative transition-all`}
                        >
                          <div
                            className={`absolute top-[2px] w-3.5 h-3.5 transition-all ${
                              campaign?.cta_enabled
                                ? 'left-[18px] bg-[#90E0EF] shadow-[0_0_6px_rgba(144,224,239,0.6)]'
                                : 'left-[2px] bg-[#555]'
                            }`}
                          />
                        </div>
                      </div>
                    </button>

                    {campaign?.cta_enabled && (
                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <FieldLabel>Button label</FieldLabel>
                          <input
                            value={campaign.cta_label || ''}
                            onChange={(e) => patchLocal({ cta_label: e.target.value })}
                            placeholder={CTA_DEFAULT_LABEL}
                            className="w-full bg-[#050505] border border-[#1a1a1a] text-white text-[12px] font-mono px-3 py-2 focus:outline-none focus:border-[#90E0EF]"
                            data-testid="dm-cta-label"
                          />
                        </div>
                        <div>
                          <FieldLabel>Button URL</FieldLabel>
                          <input
                            value={campaign.cta_url || ''}
                            onChange={(e) => patchLocal({ cta_url: e.target.value })}
                            placeholder={CTA_DEFAULT_URL}
                            className="w-full bg-[#050505] border border-[#1a1a1a] text-white text-[12px] font-mono px-3 py-2 focus:outline-none focus:border-[#90E0EF]"
                            data-testid="dm-cta-url"
                          />
                        </div>
                      </div>
                    )}
                  </SectionCard>
                </div>
              </div>

              {/* ─── RIGHT — Audience builder (5/12) ──────────────── */}
              <div className="col-span-12 lg:col-span-5 overflow-y-auto bg-[#040404]">
                <div className="px-8 py-8 space-y-6">
                  {/* Audience type */}
                  <SectionCard title="Audience type" icon={Users} testId="dm-section-audience-type">
                    <div className="grid grid-cols-2 gap-2">
                      {AUDIENCE_TYPES.map((opt) => {
                        const active = audienceType === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            onClick={() => patchLocal({ audience_type: opt.value, audience_filters: {} })}
                            className={`px-3 py-3 border text-left transition-all ${
                              active
                                ? 'border-[#90E0EF] bg-[rgba(144,224,239,0.06)] shadow-[0_0_12px_rgba(144,224,239,0.15)]'
                                : 'border-[#222] hover:border-[#3a3a3a] hover:bg-[#0c0c0c]'
                            }`}
                            data-testid={`dm-audience-${opt.value}`}
                          >
                            <div className={`text-[12px] font-outfit ${active ? 'text-[#90E0EF]' : 'text-white'}`}>
                              {opt.label}
                            </div>
                            <div className="text-[9px] font-mono uppercase tracking-[0.16em] text-[#555] mt-1">
                              {opt.value === 'tickets' && 'Ticket requests'}
                              {opt.value === 'open_call' && 'Open Call applicants'}
                              {opt.value === 'artists' && 'Artist directory'}
                              {opt.value === 'custom' && 'Hand-picked'}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </SectionCard>

                  {/* Filters per audience */}
                  {audienceType === 'tickets' && (
                    <SectionCard title="Filters" icon={Users} testId="dm-section-filters-tickets">
                      <FieldLabel>Decision status</FieldLabel>
                      <MultiSelectChips
                        options={TICKET_STATUS_OPTIONS}
                        value={campaign?.audience_filters?.decision_status || []}
                        onChange={(v) => patchFilters({ decision_status: v })}
                        testId="dm-filter-ticket-status"
                      />
                      <p className="text-[10px] font-mono text-[#555] mt-3">
                        Soft-deleted tickets are excluded automatically.
                      </p>
                    </SectionCard>
                  )}

                  {audienceType === 'open_call' && (
                    <SectionCard title="Filters" icon={Users} testId="dm-section-filters-opencall">
                      <FieldLabel>Application status</FieldLabel>
                      <MultiSelectChips
                        options={OPEN_CALL_STATUS_OPTIONS}
                        value={campaign?.audience_filters?.status || []}
                        onChange={(v) => patchFilters({ status: v })}
                      />
                      <div className="h-3" />
                      <FieldLabel>Curator rating</FieldLabel>
                      <MultiSelectChips
                        options={CURATOR_RATING_OPTIONS}
                        value={campaign?.audience_filters?.curator_rating || []}
                        onChange={(v) => patchFilters({ curator_rating: v })}
                      />
                    </SectionCard>
                  )}

                  {audienceType === 'artists' && (
                    <SectionCard title="Filters" icon={Users} testId="dm-section-filters-artists">
                      <FieldLabel>Global rating</FieldLabel>
                      <MultiSelectChips
                        options={GLOBAL_RATING_OPTIONS}
                        value={campaign?.audience_filters?.global_rating || []}
                        onChange={(v) => patchFilters({ global_rating: v })}
                      />
                      <div className="h-3" />
                      <FieldLabel>Asset submission</FieldLabel>
                      <MultiSelectChips
                        options={ASSET_STATUS_OPTIONS}
                        value={campaign?.audience_filters?.asset_submission_status || []}
                        onChange={(v) => patchFilters({ asset_submission_status: v })}
                      />
                      <div className="h-3" />
                      <FieldLabel>Exhibition relationship</FieldLabel>
                      <MultiSelectChips
                        options={RELATIONSHIP_STATUS_OPTIONS}
                        value={campaign?.audience_filters?.relationship_status || []}
                        onChange={(v) => patchFilters({ relationship_status: v })}
                      />
                    </SectionCard>
                  )}

                  {audienceType === 'custom' && (
                    <SectionCard title="Find recipients" icon={Users} testId="dm-section-filters-custom">
                      <FieldLabel>Search across Tickets · Open Call · Artists</FieldLabel>
                      <input
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        placeholder="Search name or email…"
                        className="w-full bg-[#050505] border border-[#1a1a1a] text-white text-[12px] font-mono px-3 py-2 focus:outline-none focus:border-[#90E0EF]"
                        data-testid="dm-custom-search"
                      />
                      {searchResults.length > 0 && (
                        <div className="mt-3 border border-[#161616] max-h-60 overflow-y-auto">
                          {searchResults.map((r) => {
                            const selected = manualIds.some((m) => m.source_type === r.source_type && m.source_id === r.source_id);
                            return (
                              <button
                                type="button"
                                key={`${r.source_type}-${r.source_id}`}
                                onClick={() => toggleManual(r)}
                                className={`w-full text-left px-3 py-2 flex items-center justify-between gap-2 border-b border-[#0e0e0e] hover:bg-[#0d0d0d] transition-all ${selected ? 'bg-[rgba(144,224,239,0.05)]' : ''}`}
                              >
                                <span className="flex items-center gap-2 min-w-0">
                                  <SourceBadge source={r.source_type} />
                                  <span className="text-[12px] font-outfit text-white truncate">{r.name || '—'}</span>
                                  <span className="text-[10px] font-mono text-[#666] truncate">{r.email}</span>
                                </span>
                                <span className={`text-[10px] font-mono uppercase tracking-[0.16em] ${selected ? 'text-[#90E0EF]' : 'text-[#555]'}`}>
                                  {selected ? 'Selected' : 'Add'}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {manualIds.length > 0 && (
                        <div className="mt-4">
                          <FieldLabel>Selected · {manualIds.length}</FieldLabel>
                          <div className="max-h-52 overflow-y-auto border border-[#161616]">
                            {manualIds.map((m) => (
                              <div
                                key={`${m.source_type}-${m.source_id}`}
                                className="px-3 py-2 flex items-center justify-between gap-2 border-b border-[#0e0e0e]"
                              >
                                <span className="flex items-center gap-2 min-w-0">
                                  <SourceBadge source={m.source_type} />
                                  <span className="text-[12px] font-outfit text-white truncate">{m.name || '—'}</span>
                                  <span className="text-[10px] font-mono text-[#666] truncate">{m.email}</span>
                                </span>
                                <button
                                  onClick={() => removeManual(m)}
                                  className="text-[#e88ba0] hover:text-white"
                                  title="Remove"
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </SectionCard>
                  )}

                  {audienceType === 'invite_prospects' && (
                    <SectionCard
                      title="Invite Prospects filters"
                      icon={Users}
                      helper="Reads from Invite OS saved search results + invite contacts (suppression-aware)."
                      testId="dm-section-filters-invite"
                    >
                      <FieldLabel>Category</FieldLabel>
                      <MultiSelectChips
                        options={INVITE_CATEGORY_OPTIONS}
                        value={campaign?.audience_filters?.category || []}
                        onChange={(v) => patchFilters({ category: v })}
                        testId="dm-filter-invite-category"
                      />
                      <div className="h-3" />
                      <FieldLabel>Prospect status</FieldLabel>
                      <MultiSelectChips
                        options={INVITE_PROSPECT_STATUS_OPTIONS}
                        value={campaign?.audience_filters?.status || []}
                        onChange={(v) => patchFilters({ status: v })}
                      />
                      <div className="mt-4 space-y-2">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={campaign?.audience_filters?.include_existing_contacts !== false}
                            onChange={(e) => patchFilters({ include_existing_contacts: e.target.checked })}
                            className="accent-[#90E0EF]"
                            data-testid="dm-filter-invite-include-contacts"
                          />
                          <span className="text-[11px] font-outfit text-[#ccc]">Include established Invite contacts</span>
                        </label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!campaign?.audience_filters?.include_already_invited}
                            onChange={(e) => patchFilters({ include_already_invited: e.target.checked })}
                            className="accent-[#90E0EF]"
                            data-testid="dm-filter-invite-include-already"
                          />
                          <span className="text-[11px] font-outfit text-[#ccc]">Include prospects already invited before</span>
                        </label>
                      </div>
                      <p className="text-[10px] font-mono text-[#555] mt-3 leading-relaxed">
                        Suppressed / bounced / unsubscribed / do-not-contact records are always excluded.
                      </p>
                    </SectionCard>
                  )}

                  {/* Audience preview */}
                  <section
                    data-testid="dm-audience-preview"
                    className="border border-[#1a1a1a] bg-[#070707] p-6"
                  >
                    <header className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.32em] text-[#90E0EF]">
                          <Users className="w-3.5 h-3.5" /> Audience preview
                        </h3>
                        <p className="mt-1 text-[10px] font-mono text-[#555]">
                          Live · de-duplicated by email.
                        </p>
                      </div>
                      {audiencePreviewLoading && (
                        <Loader2 className="w-4 h-4 animate-spin text-[#90E0EF]" />
                      )}
                    </header>

                    <div className="mt-4 flex items-baseline gap-3" data-testid="dm-audience-total">
                      <span className="text-4xl font-outfit text-white tabular-nums">
                        {totalRecipients !== null ? totalRecipients : '—'}
                      </span>
                      <span className="text-[11px] font-mono text-[#666] uppercase tracking-[0.18em]">
                        recipient(s)
                      </span>
                    </div>

                    {audiencePreview?.recipients?.length > 0 && (
                      <div className="mt-4 max-h-72 overflow-y-auto border border-[#111] divide-y divide-[#0e0e0e]" data-testid="dm-audience-list">
                        {audiencePreview.recipients.slice(0, 50).map((r, i) => (
                          <div
                            key={`${r.source_type}-${r.source_id || i}-${r.email}`}
                            className="group flex items-center gap-2 px-3 py-2 hover:bg-[#0c0c0c]"
                          >
                            <SourceBadge source={r.source_type} />
                            <span className="text-[12px] font-outfit text-white truncate flex-1">
                              {r.name || '—'}
                              {r.category && (
                                <span className="ml-2 text-[10px] font-mono text-[#7ed4a0] uppercase tracking-[0.14em]">
                                  · {r.category.replace(/_/g, ' ')}
                                </span>
                              )}
                            </span>
                            <span className="text-[10px] font-mono text-[#666] truncate max-w-[200px]">{r.email}</span>
                            <button
                              type="button"
                              onClick={() => excludeRecipient(r)}
                              data-testid={`dm-exclude-${(r.email || '').replace(/[^a-z0-9]/gi, '_')}`}
                              title="Exclude from this message only — does not delete the record"
                              className="text-[#888] hover:text-[#e88ba0] px-1.5 py-0.5 border border-[#222] hover:border-[rgba(232,139,160,0.45)] inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.14em] transition-colors"
                            >
                              <UserMinus className="w-3 h-3" /> Exclude
                            </button>
                          </div>
                        ))}
                        {audiencePreview.total > 50 && (
                          <p className="px-3 py-2 text-[10px] font-mono text-[#555]">
                            + {audiencePreview.total - 50} more · use filters to narrow
                          </p>
                        )}
                      </div>
                    )}
                    {audiencePreview?.error && (
                      <p className="mt-3 text-[10px] font-mono text-[#e88ba0]">{audiencePreview.error}</p>
                    )}

                    {/* ─── Final-audience confirmation gate ─────────
                        Server-side LOCK. Ticking snapshots the resolved
                        recipient list into the campaign so Send/Schedule
                        dispatch ONLY to that frozen group, never to the
                        live audience. Edits to filters / type clear the
                        lock automatically. */}
                    {showStaleWarning && (
                      <div
                        className="mt-4 border border-[rgba(232,139,160,0.6)] bg-[rgba(232,139,160,0.08)] p-3"
                        data-testid="dm-audience-stale-warning"
                      >
                        <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#e88ba0]">
                          Recipient group changed — please confirm again
                        </div>
                        <div className="text-[10px] font-mono text-[#aaa] mt-1 leading-relaxed">
                          You edited the audience after locking. Save the draft
                          to clear the previous snapshot, then re-confirm.
                        </div>
                      </div>
                    )}
                    {lockedSnapshot && !showStaleWarning && (
                      <div
                        className="mt-4 border border-[rgba(126,212,160,0.55)] bg-[rgba(126,212,160,0.06)] p-3"
                        data-testid="dm-audience-locked-badge"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0">
                            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#7ed4a0] inline-flex items-center gap-2">
                              <CheckCircle2 className="w-3.5 h-3.5" />
                              Recipient group locked · {lockedSnapshot.count} recipient{lockedSnapshot.count === 1 ? '' : 's'}
                            </div>
                            <div className="text-[10px] font-mono text-[#777] mt-1.5 leading-relaxed">
                              Send Now / Schedule will dispatch ONLY to this
                              snapshot — never the live audience. Editing
                              filters or audience type clears the lock.
                              {lockedSnapshot.confirmed_at && (
                                <>
                                  {' '}
                                  Confirmed{' '}
                                  <span className="text-[#aaa]">
                                    {new Date(lockedSnapshot.confirmed_at).toLocaleString()}
                                  </span>.
                                </>
                              )}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleUnlockAudience}
                            disabled={confirmBusy}
                            data-testid="dm-audience-unlock"
                            className="shrink-0 text-[9px] font-mono uppercase tracking-[0.2em] px-2 py-1 border border-[#222] text-[#aaa] hover:border-[#555] hover:text-white disabled:opacity-40"
                          >
                            Unlock
                          </button>
                        </div>
                      </div>
                    )}
                    {!lockedSnapshot && totalRecipients !== null && totalRecipients > 0 && (
                      <div
                        className="mt-4 border border-[rgba(228,192,122,0.45)] bg-[rgba(228,192,122,0.04)] p-3"
                        data-testid="dm-audience-confirmation"
                      >
                        <label className="flex items-start gap-3 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={false}
                            onChange={(e) => {
                              if (e.target.checked) handleConfirmAudience();
                            }}
                            disabled={confirmBusy}
                            className="mt-0.5 w-4 h-4 accent-[#7ed4a0]"
                            data-testid="dm-audience-confirm-checkbox"
                          />
                          <div>
                            <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#e4c07a] inline-flex items-center gap-2">
                              {confirmBusy && <Loader2 className="w-3 h-3 animate-spin" />}
                              I confirm this is the final recipient group
                            </div>
                            <div className="text-[10px] font-mono text-[#777] mt-1 leading-relaxed">
                              Snapshots the {totalRecipients} resolved
                              recipient{totalRecipients === 1 ? '' : 's'} as
                              the locked send list. Send Now and Schedule
                              dispatch only to this list. Editing filters or
                              audience type clears the lock automatically.
                            </div>
                          </div>
                        </label>
                      </div>
                    )}
                    {totalRecipients === 0 && (
                      <div
                        className="mt-4 border border-[#222] p-3 text-[10px] font-mono text-[#777] leading-relaxed"
                        data-testid="dm-audience-empty-note"
                      >
                        No recipients match the current audience. Adjust filters
                        before scheduling or sending.
                      </div>
                    )}

                    {excludedRecipients.length > 0 && (
                      <div className="mt-4 border border-[rgba(232,139,160,0.25)] bg-[rgba(232,139,160,0.04)] p-3" data-testid="dm-excluded-section">
                        <div className="flex items-center justify-between">
                          <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#e88ba0] inline-flex items-center gap-1.5">
                            <UserMinus className="w-3 h-3" />
                            <span data-testid="dm-excluded-count">{excludedRecipients.length}</span> excluded from this message
                          </div>
                          <button
                            type="button"
                            onClick={clearExclusions}
                            data-testid="dm-excluded-clear"
                            className="text-[9px] font-mono uppercase tracking-[0.16em] text-[#888] hover:text-white inline-flex items-center gap-1"
                          >
                            <Undo2 className="w-3 h-3" /> Restore all
                          </button>
                        </div>
                        <div className="mt-2 max-h-44 overflow-y-auto divide-y divide-[rgba(232,139,160,0.12)]">
                          {excludedRecipients.map((m, i) => (
                            <div
                              key={`${m.source_type || 'x'}-${m.source_id || i}-${m.email}`}
                              className="flex items-center gap-2 px-1 py-1.5"
                            >
                              <SourceBadge source={m.source_type || 'ticket'} />
                              <span className="text-[11px] font-outfit text-white truncate flex-1">{m.name || '—'}</span>
                              <span className="text-[10px] font-mono text-[#888] truncate">{m.email}</span>
                              <button
                                type="button"
                                onClick={() => restoreRecipient(m)}
                                title="Restore"
                                className="text-[#888] hover:text-[#90E0EF] inline-flex items-center gap-1 text-[9px] font-mono uppercase tracking-[0.14em]"
                              >
                                <Undo2 className="w-3 h-3" /> Restore
                              </button>
                            </div>
                          ))}
                        </div>
                        <p className="mt-2 text-[10px] font-mono text-[#666] leading-relaxed">
                          Exclusion is per-message. The underlying ticket / open-call / artist / invite record is never modified.
                        </p>
                      </div>
                    )}
                  </section>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ─── Bottom sticky action bar ─────────────────────────────
            Two clearly separated groups (left = save/preview/test, right =
            schedule + send). Extra right padding so the buttons never sit
            under the platform's preview/update overlay in the corner. */}
        <div className="border-t border-[#161616] bg-[#070707] px-8 pr-16 lg:pr-32 xl:pr-56 py-4 flex flex-wrap items-center gap-x-3 gap-y-3">
          {/* Left group: Save / Preview / Test */}
          <button
            onClick={() => saveDraft({})}
            disabled={saving}
            data-testid="dm-save-draft"
            className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] px-4 py-2.5 border border-[#222] text-[#aaa] hover:text-white hover:border-[#444] disabled:opacity-50 transition-all"
          >
            <Save className="w-3.5 h-3.5" />
            {saving ? 'Saving…' : 'Save draft'}
          </button>
          <button
            onClick={openPreview}
            data-testid="dm-preview-btn"
            className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] px-4 py-2.5 border border-[#90E0EF]/40 text-[#90E0EF] hover:bg-[#90E0EF]/10 hover:border-[#90E0EF] transition-all"
          >
            <Eye className="w-3.5 h-3.5" /> Preview
          </button>
          <div className="inline-flex items-stretch border border-[#222]">
            <span className="px-3 py-2.5 inline-flex items-center gap-1.5 border-r border-[#222] text-[#777]">
              <FlaskConical className="w-3.5 h-3.5" />
              <span className="text-[10px] font-mono uppercase tracking-[0.16em]">Test to</span>
            </span>
            <input
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              className="bg-transparent text-[11px] font-mono text-white px-3 py-2.5 focus:outline-none focus:bg-[#0c0c0c] w-[220px]"
              placeholder="test@example.com"
              data-testid="dm-test-email"
            />
            <button
              onClick={sendTest}
              data-testid="dm-test-send"
              className="text-[10px] font-mono uppercase tracking-[0.2em] px-4 py-2.5 border-l border-[#222] text-[#aaa] hover:text-white hover:bg-[#0c0c0c] transition-all"
            >
              Send test
            </button>
          </div>

          {/* Right group: Schedule + Send Now */}
          <div className="ml-auto flex items-center gap-3 flex-wrap" data-testid="dm-action-right-group">
            {/* Inline guidance when buttons are locked */}
            {(!audienceConfirmed || !(totalRecipients > 0) || showStaleWarning) && (
              <span
                className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#777] hidden md:inline-block"
                data-testid="dm-action-locked-hint"
              >
                {showStaleWarning
                  ? 'Recipient group changed — confirm again'
                  : totalRecipients > 0
                    ? 'Confirm the recipient group to unlock'
                    : 'No recipients yet'}
              </span>
            )}
            <button
              onClick={() => setScheduling(true)}
              disabled={!audienceConfirmed || !(totalRecipients > 0) || showStaleWarning}
              data-testid="dm-schedule"
              className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] px-4 py-2.5 border border-[rgba(228,192,122,0.45)] text-[#e4c07a] hover:bg-[rgba(228,192,122,0.06)] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-all whitespace-nowrap"
            >
              <CalendarClock className="w-3.5 h-3.5" />
              {scheduledLabel ? `Reschedule · ${scheduledLabel}` : 'Schedule'}
            </button>
            <button
              onClick={sendNow}
              disabled={!audienceConfirmed || !(totalRecipients > 0) || showStaleWarning}
              data-testid="dm-send-now"
              className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.22em] px-6 py-2.5 bg-[#90E0EF] text-black hover:bg-[#a8e8f4] shadow-[0_0_18px_rgba(144,224,239,0.3)] disabled:bg-[#1a1a1a] disabled:text-[#555] disabled:shadow-none disabled:cursor-not-allowed transition-all whitespace-nowrap"
            >
              <Send className="w-3.5 h-3.5" /> Send now
            </button>
          </div>
        </div>
      </motion.div>

      {/* ─── Preview overlay ────────────────────────────────────────── */}
      {showPreview && (
        <motion.div
          key="dm-preview"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-8 lg:p-12"
          onClick={() => setShowPreview(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            data-testid="dm-preview-modal"
            className="bg-[#070707] border border-[#1a1a1a] w-full max-w-[1240px] h-[calc(100vh-3rem)] sm:h-[calc(100vh-4rem)] lg:h-[calc(100vh-6rem)] flex flex-col shadow-[0_0_80px_rgba(144,224,239,0.08)]"
          >
            <div className="px-7 py-5 border-b border-[#1a1a1a] flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="text-[11px] font-mono uppercase tracking-[0.36em] text-[#90E0EF] inline-flex items-center gap-2">
                  <Eye className="w-3.5 h-3.5" /> Email Preview
                </div>
                <div className="text-[16px] font-outfit text-white mt-1.5 truncate">{previewSubject || '—'}</div>
                <div className="text-[10px] font-mono text-[#555] uppercase tracking-[0.18em] mt-1">
                  Branded layout · 600px gallery email · logo + body + hero + CTA + footer
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => { navigator.clipboard?.writeText(previewHtml); toast.success('HTML copied'); }}
                  data-testid="dm-preview-copy"
                  className="text-[10px] font-mono uppercase tracking-[0.18em] px-4 py-2 border border-[#222] text-[#aaa] hover:text-white hover:border-[#444] transition-all inline-flex items-center gap-1.5"
                >
                  <Copy className="w-3.5 h-3.5" /> Copy HTML
                </button>
                <button
                  onClick={() => setShowPreview(false)}
                  data-testid="dm-preview-close"
                  className="w-9 h-9 border border-[#222] flex items-center justify-center text-[#777] hover:text-white hover:border-[#444]"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            {/* Centered iframe with the gallery "room" framing for context. */}
            <div className="flex-1 overflow-auto bg-[#050505] flex items-start justify-center p-4 sm:p-8">
              <iframe
                title="dm-preview"
                srcDoc={previewHtml}
                className="w-full max-w-[720px] h-full bg-black border border-[#1a1a1a] shadow-[0_8px_40px_rgba(0,0,0,0.6)]"
                style={{ minHeight: '92%' }}
              />
            </div>
          </div>
        </motion.div>
      )}

      {/* Schedule modal */}
      <ScheduleModal
        open={scheduling}
        onClose={() => setScheduling(false)}
        onConfirm={schedule}
        busy={scheduleBusy}
      />
    </AnimatePresence>
  );
}
